import {BatchStateValue} from '@/types/batch-types'
import type {Router} from 'vue-router'
import {getBatchById, getReturnedBatchById} from "@/api/batches";
import {getRollById} from "@/api/rolls";

/**
 * Determines the appropriate route path for a batch based on its state and type
 * @param batch The batch object to get the route for
 * @returns The route path string
 */
export function getBatchRoutePath(batch: any): string {
    if (!batch) return '';

    // For regular batches, check the state
    switch (batch.state) {
        case BatchStateValue.GREIG:
        case BatchStateValue.OFFSITE:
            return `/batches/${batch.id}`;
        case BatchStateValue.FINISHED:
            // If it has a returnedBatchId, we should navigate to the returned batch
            if (batch.returnedBatchId) {
                return `/batches/returned-batch-details/${batch.returnedBatchId}`;
            } else {
                return `/batches/${batch.id}`;
            }
        case BatchStateValue.AVAILABLE:
            // For Available state, we should navigate to available-batches-details
            // If it has a returnedBatchId, use that instead of batch.id
            if (batch.returnedBatchId) {
                return `/batches/available-batches-details/${batch.returnedBatchId}`;
            } else {
                return `/batches/available-batches-details/${batch.id}`;
            }
        case BatchStateValue.DISPATCHED:
            // For Dispatched state, we should navigate to dispatched-batches-details
            // If it has a returnedBatchId, use that instead of batch.id
            if (batch.returnedBatchId) {
                return `/batches/dispatched-batches-details/${batch.returnedBatchId}`;
            } else {
                return `/batches/dispatched-batches-details/${batch.id}`;
            }
        default:
            return `/batches/${batch.id}`;
    }
}

/**
 * For programmatic navigation (when RouterLink can't be used)
 * @param batch The batch object to navigate to
 * @param router The Vue Router instance
 */
export function navigateToBatchDetails(batch: any, router: Router): void {
    const path = getBatchRoutePath(batch);
    if (path) {
        router.push(path);
    }
}

export async function navigateToBatch(router: Router, batchId: string) {
    try {
        try {
            const batch = await getBatchById(batchId)
            window.location.href = getBatchRoutePath(batch)
            return
        } catch {
            try {
                await getReturnedBatchById(batchId)
                window.location.href = `/batches/returned-batch-details/${batchId}`
                return
            } catch (error) {
                console.error('Batch not found anywhere:', batchId)
                throw new Error(`Batch ${batchId} not found`)
            }
        }
    } catch (error) {
        console.error('Error navigating to batch:', error)
        throw error
    }
}


/**
 * Navigate to the original batch by first getting the roll details, then the previous batch
 * @param router The Vue Router instance
 * @param rollId The roll ID to look up
 */
export async function navigateToBatchByRollId(router: Router, rollId: string) {
    try {
        const roll = await getRollById(rollId)

        const previousBatchId = roll.previousBatchId
        if (!previousBatchId) {
            throw new Error('Roll does not have a previousBatchId')
        }

        await navigateToBatch(router, previousBatchId)

    } catch (error) {
        console.error('Error navigating to original batch via roll ID:', error)
        throw error
    }
}